import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, X, Check, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const VISIBILITA = {
  commessa:  { label: "Commessa (tutti i membri)", col: "#7c3aed" },
  personale: { label: "Solo io", col: "#6b7280" },
  coinvolti: { label: "Utenti specifici", col: "#2563eb" },
};

function EventoModal({ evento, day, project, utenti, onClose, onSaved }) {
  const { currentUser, userProfile } = useAuth();
  const nomeAutore = userProfile?.displayName || currentUser?.displayName || "Utente";

  const [form, setForm] = useState(evento || {
    titolo: "",
    descrizione: "",
    oraInizio: "09:00",
    oraFine: "10:00",
    visibilita: "commessa",
    coinvoltiUid: [],
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const salva = async () => {
    if (!form.titolo.trim()) return;
    setSaving(true);
    const dati = {
      titolo: form.titolo.trim(),
      descrizione: form.descrizione || "",
      dueDate: format(day, "yyyy-MM-dd"),
      oraInizio: form.oraInizio || "",
      oraFine: form.oraFine || "",
      visibilita: form.visibilita,
      coinvoltiUid: form.coinvoltiUid || [],
      projectId: project.id,
      projectName: project.name || "",
      clienteId: project.clienteId || "",
      clienteNome: project.clienteNome || "",
      responsabileUid: project.responsabileUid || "",
      responsabileNome: project.responsabileNome || "",
      organizationId: project.organizationId || project.orgId,
      autoreUid: currentUser?.uid,
      autoreNome: nomeAutore,
      updatedAt: serverTimestamp(),
    };
    if (evento?.id) {
      await updateDoc(doc(db, "eventiCommessa", evento.id), dati);
    } else {
      await addDoc(collection(db, "eventiCommessa"), { ...dati, createdAt: serverTimestamp() });
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  const inp = { width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>{evento ? "Modifica evento" : "Nuovo evento"}</h3>
          <button className="btn-icon" onClick={onClose}><X size={14}/></button>
        </div>
        <div style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, marginBottom: 12 }}>
          📅 {format(day, "EEEE d MMMM yyyy", { locale: it })}
        </div>
        <div className="form-group">
          <label className="form-label">Titolo *</label>
          <input style={inp} value={form.titolo} onChange={e => upd("titolo", e.target.value)} placeholder="Es. Consegna quadri elettrici" autoFocus/>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div><label className="form-label">Ora inizio</label><input type="time" style={inp} value={form.oraInizio} onChange={e => upd("oraInizio", e.target.value)}/></div>
          <div><label className="form-label">Ora fine</label><input type="time" style={inp} value={form.oraFine} onChange={e => upd("oraFine", e.target.value)}/></div>
        </div>
        <div className="form-group">
          <label className="form-label">Descrizione</label>
          <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={form.descrizione} onChange={e => upd("descrizione", e.target.value)} placeholder="Dettagli sull'evento..."/>
        </div>
        <div className="form-group">
          <label className="form-label">Visibilità</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(VISIBILITA).map(([k, v]) => (
              <button key={k} type="button" onClick={() => upd("visibilita", k)}
                style={{ padding: "4px 12px", borderRadius: 20, border: `1px solid ${form.visibilita === k ? v.col : "#e5e7eb"}`,
                  background: form.visibilita === k ? v.col : "#fff", color: form.visibilita === k ? "#fff" : "#6b7280",
                  fontSize: 11, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontWeight: form.visibilita === k ? 700 : 400 }}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
        {form.visibilita === "coinvolti" && utenti.length > 0 && (
          <div className="form-group">
            <label className="form-label">Utenti coinvolti</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {utenti.map(u => {
                const sel = (form.coinvoltiUid || []).includes(u.uid);
                return (
                  <button key={u.uid} type="button"
                    onClick={() => upd("coinvoltiUid", sel
                      ? (form.coinvoltiUid || []).filter(x => x !== u.uid)
                      : [...(form.coinvoltiUid || []), u.uid])}
                    style={{ padding: "3px 10px", borderRadius: 20, border: `1px solid ${sel ? "#2563eb" : "#e5e7eb"}`,
                      background: sel ? "#dbeafe" : "#fff", color: sel ? "#1d4ed8" : "#6b7280",
                      fontSize: 11, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontWeight: sel ? 700 : 400 }}>
                    {u.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={salva} disabled={!form.titolo.trim() || saving}>
            {saving ? "..." : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProjectCalendar({ project, onSelectTask }) {
  const { currentUser, userProfile } = useAuth();
  const [eventi, setEventi] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [current, setCurrent] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editEvento, setEditEvento] = useState(null);
  const [utenti, setUtenti] = useState([]);

  // Carica eventi commessa (nuova collezione)
  useEffect(() => {
    if (!project?.id || !project?.organizationId) return;
    return onSnapshot(
      query(collection(db, "eventiCommessa"), where("projectId", "==", project.id), where("organizationId","==",project.organizationId)),
      snap => setEventi(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [project?.id, project?.organizationId]);

  // Carica tasks con dueDate (retrocompatibilità)
  useEffect(() => {
    if (!project?.id || !project?.organizationId) return; // aperta solo per id: aspetto i dati
    const q = query(collection(db, "tasks"), where("projectId", "==", project.id), where("organizationId","==",project.organizationId));
    return onSnapshot(q, snap => setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [project?.id, project?.organizationId]);

  // Carica utenti organizzazione
  useEffect(() => {
    if (!project?.organizationId && !project?.orgId) return;
    const orgId = project.organizationId || project.orgId;
    onSnapshot(query(collection(db, "users"), where("organizationId", "==", orgId)),
      snap => setUtenti(snap.docs.map(d => ({ uid: d.data().uid, displayName: d.data().displayName }))));
  }, [project?.organizationId, project?.orgId]);

  const getEventiGiorno = date => {
    const s = format(date, "yyyy-MM-dd");
    return eventi.filter(e => e.dueDate === s);
  };

  const getTasksGiorno = date => {
    const s = format(date, "yyyy-MM-dd");
    return tasks.filter(t => {
      if (t.startDate) return t.startDate <= s && s <= t.dueDate;
      return t.dueDate === s;
    });
  };

  const eliminaEvento = async (id) => {
    await deleteDoc(doc(db, "eventiCommessa", id));
  };

  const renderGrid = () => {
    const start = startOfWeek(startOfMonth(current), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(current), { weekStartsOn: 1 });
    const today = new Date();
    const rows = [];
    let day = start;
    while (day <= end) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(day);
        const evG = getEventiGiorno(d);
        const tG = getTasksGiorno(d);
        const isToday = isSameDay(d, today);
        const isCurrentMonth = isSameMonth(d, current);
        const isSelected = selectedDay && isSameDay(d, selectedDay);
        const isSabDom = d.getDay() === 0 || d.getDay() === 6;
        week.push(
          <div key={d.toString()}
            style={{ border: `2px solid ${isSelected ? "#7c3aed" : isToday ? "#c4b5fd" : "#f3f4f6"}`,
              borderRadius: 8, padding: "4px 6px", minHeight: 80,
              background: isSelected ? "#faf5ff" : isSabDom ? "#fafafa" : "#fff", cursor: "pointer" }}
            onClick={() => { setSelectedDay(d); setShowAdd(false); setEditEvento(null); }}>
            <div style={{ fontSize: 11, fontWeight: isToday ? 800 : 500,
              color: isToday ? "#7c3aed" : !isCurrentMonth ? "#d1d5db" : isSabDom ? "#9ca3af" : "#374151", marginBottom: 4 }}>
              {isToday
                ? <span style={{ background: "#7c3aed", color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{format(d, "d")}</span>
                : format(d, "d")}
            </div>
            {evG.slice(0, 2).map(ev => (
              <div key={ev.id} style={{ fontSize: 10, fontWeight: 600, padding: "2px 5px", borderRadius: 4, marginBottom: 2,
                background: "#ede9fe", color: "#5b21b6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ev.oraInizio && <span style={{ opacity: 0.7 }}>{ev.oraInizio} </span>}{ev.titolo}
              </div>
            ))}
            {tG.slice(0, 1).map(t => (
              <div key={t.id} style={{ fontSize: 10, padding: "2px 5px", borderRadius: 4, marginBottom: 2,
                background: ["completata", "completed"].includes(t.status) ? "#f3f4f6" : "#d1fae5", color: ["completata", "completed"].includes(t.status) ? "#9ca3af" : "#059669",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.title}
              </div>
            ))}
            {(evG.length + tG.length) > 3 && <div style={{ fontSize: 9, color: "#9ca3af" }}>+{evG.length + tG.length - 3} altri</div>}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(<React.Fragment key={day.toString()}>{week}</React.Fragment>);
    }
    return rows;
  };

  const selectedEventi = selectedDay ? getEventiGiorno(selectedDay) : [];
  const selectedTasks = selectedDay ? getTasksGiorno(selectedDay) : [];

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden", minWidth: 0 }}>
      {/* Calendario */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20, minWidth: 600 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <button onClick={() => setCurrent(subMonths(current, 1))}
            style={{ padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <ChevronLeft size={16}/>
          </button>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: "#1e1b4b", textTransform: "capitalize" }}>
            {format(current, "MMMM yyyy", { locale: it })}
          </h3>
          <button onClick={() => setCurrent(addMonths(current, 1))}
            style={{ padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <ChevronRight size={16}/>
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 4 }}>
          {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(d => (
            <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#9ca3af", padding: "4px 0", textTransform: "uppercase" }}>{d}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, flex: 1 }}>
          {renderGrid()}
        </div>
      </div>

      {/* Panel laterale */}
      {selectedDay && (
        <div style={{ width: 320, borderLeft: "1px solid #e5e7eb", background: "#fff", display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", background: "#1e1b4b" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#fff", textTransform: "capitalize" }}>
              {format(selectedDay, "EEEE d MMMM", { locale: it })}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
              {selectedEventi.length} evento/i · {selectedTasks.length} attività
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
            {/* Eventi commessa */}
            {selectedEventi.map(ev => (
              <div key={ev.id} style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#5b21b6" }}>
                      {ev.oraInizio && <span style={{ color: "#7c3aed", marginRight: 6 }}>{ev.oraInizio}{ev.oraFine && `→${ev.oraFine}`}</span>}
                      {ev.titolo}
                    </div>
                    {ev.descrizione && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{ev.descrizione}</div>}
                    <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
                      ✏️ {ev.autoreNome} · {VISIBILITA[ev.visibilita]?.label}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => setEditEvento(ev)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 11 }}>✏️</button>
                    <button onClick={() => eliminaEvento(ev.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {/* Task con scadenza */}
            {selectedTasks.map(t => (
              <div key={t.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: ["completata", "completed"].includes(t.status) ? "#9ca3af" : "#111827",
                  textDecoration: ["completata", "completed"].includes(t.status) ? "line-through" : "none", cursor: "pointer" }}
                  onClick={() => onSelectTask(t)}>
                  {t.title}
                </div>
                {t.assignedName && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>👤 {t.assignedName}</div>}
              </div>
            ))}
            {selectedEventi.length === 0 && selectedTasks.length === 0 && !showAdd && (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#9ca3af", fontSize: 12 }}>Nessun evento per questo giorno</div>
            )}
          </div>
          <div style={{ padding: "10px 12px", borderTop: "1px solid #e5e7eb" }}>
            <button onClick={() => { setShowAdd(true); setEditEvento(null); }}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "8px", border: "1px dashed #7c3aed", borderRadius: 8, background: "#faf5ff",
                color: "#7c3aed", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              <Plus size={13}/> Nuovo evento per questo giorno
            </button>
          </div>
        </div>
      )}

      {/* Modal crea/modifica evento */}
      {(showAdd || editEvento) && selectedDay && (
        <EventoModal
          evento={editEvento}
          day={selectedDay}
          project={project}
          utenti={utenti}
          onClose={() => { setShowAdd(false); setEditEvento(null); }}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}
